/**
 * The per-group checkbox list shared by the export and import panels.
 *
 * Deliberately NOT instrumented per checkbox: these are transient selection
 * state for a single action, not persisted preference toggles, and the choice
 * they express is already reported in the `{ groups }` property of
 * `feature:settings_export` / `feature:settings_import`. Firing seven toggle
 * events per export would inflate the per-event budget F01 flags without
 * telling us anything the success event does not.
 */
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import type { SettingsBundleGroupId } from '../model/settingsBundleManifest';

export type ChecklistRow = {
  id: SettingsBundleGroupId | 'metaInfo';
  label: string;
  description: string;
  /** Never ticked by default, on either panel. */
  optIn?: boolean;
  /** Absent from the file / nothing to export — shown greyed with a note. */
  unavailable?: string;
};

export function SettingsGroupChecklist({
  rows,
  selected,
  onToggle,
  idPrefix,
}: {
  rows: ChecklistRow[];
  selected: ReadonlySet<string>;
  onToggle: (id: string, next: boolean) => void;
  idPrefix: string;
}) {
  if (rows.length === 0) {
    return (
      <div className="text-xs text-muted-foreground" data-testid={`${idPrefix}-empty`}>
        Nothing available.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => {
        const controlId = `${idPrefix}-${row.id}`;
        return (
          <div
            key={row.id}
            className="flex items-start gap-3 rounded-md border border-border bg-background p-3"
          >
            <Checkbox
              id={controlId}
              className="mt-0.5"
              checked={selected.has(row.id)}
              disabled={Boolean(row.unavailable)}
              onCheckedChange={(checked) => onToggle(row.id, checked === true)}
              data-testid={controlId}
            />
            <div className="grid gap-0.5">
              <Label htmlFor={controlId} className="cursor-pointer text-sm font-medium">
                {row.label}
              </Label>
              <div className="text-xs text-muted-foreground">
                {row.unavailable ?? row.description}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
